import React from "react";
import PageHeader from "../../common/PageHeader";
import {
  Language,
  SaveOutlined,
  UploadFileOutlined,
  PrivacyTipOutlined,
  GavelOutlined,
  InfoOutlined,
  BrushOutlined,
  SearchOutlined,
  ShareOutlined,
  BarChartOutlined,
  HomeOutlined,
  AttachMoneyOutlined,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import {
  Box,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Button,
  CircularProgress,
  Avatar,
  Stack,
  useTheme,
  alpha,
  Chip,
  Card,
  CardContent,
  Divider,
} from "@mui/material";

const Section = ({ title, subtitle, icon: Icon, iconColor, children }) => {
  const theme = useTheme();
  const color = iconColor || theme.palette.primary.main;

  return (
    <Card
      sx={{
        mb: 3,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        overflow: "hidden",
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header row */}
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: alpha(color, 0.12),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {Icon && <Icon sx={{ fontSize: 20, color }} />}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {children}
      </CardContent>
    </Card>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SiteSettings = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();

  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState({});

  const [form, setForm] = React.useState({
    site_name: "",
    site_logo: "",
    site_favicon: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    og_title: "",
    og_description: "",
    og_image: "",
    google_analytics_id: "",
    google_tag_manager_id: "",
    facebook_pixel_id: "",
    custom_homepage_enabled: false,
    custom_homepage_url: "",
    youtube_tutorial_url: "",
    currency_symbol: "$",
    currency_code: "USD",
    currency_exchange_rate: "1.0000",
    privacy_policy_html: "",
    tnc_html: "",
    about_us_html: "",
  });

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  // ── Load ──────────────────────────────────────────────────────────────────
  React.useEffect(() => {
    (async () => {
      const res = await hitAxios({
        path: "/api/web/get_web_all",
        post: false,
        admin: true,
        showSnackbar: false,
      });
      if (res?.data?.success && res.data.data) {
        const d = res.data.data;
        setForm({
          site_name: d.site_name || "",
          site_logo: d.site_logo || "",
          site_favicon: d.site_favicon || "",
          meta_title: d.meta_title || "",
          meta_description: d.meta_description || "",
          meta_keywords: d.meta_keywords || "",
          og_title: d.og_title || "",
          og_description: d.og_description || "",
          og_image: d.og_image || "",
          google_analytics_id: d.google_analytics_id || "",
          google_tag_manager_id: d.google_tag_manager_id || "",
          facebook_pixel_id: d.facebook_pixel_id || "",
          custom_homepage_enabled:
            d.custom_homepage_enabled === "1" ||
            d.custom_homepage_enabled === 1,
          custom_homepage_url: d.custom_homepage_url || "",
          youtube_tutorial_url: d.youtube_tutorial_url || "",
          currency_symbol: d.currency_symbol || "$",
          currency_code: d.currency_code || "USD",
          currency_exchange_rate: d.currency_exchange_rate || "1.0000",
          privacy_policy_html: d.privacy_policy_html || "",
          tnc_html: d.tnc_html || "",
          about_us_html: d.about_us_html || "",
        });
      }
    })();
  }, []);

  // ── File upload ───────────────────────────────────────────────────────────
  const handleFileChange = async (e, field, type = "media") => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading((p) => ({ ...p, [field]: true }));
    const fd = new FormData();
    fd.append("file", file);
    const res = await hitAxios({
      path:
        type === "favicon"
          ? "/api/web/upload_favicon"
          : "/api/web/upload_media",
      post: true,
      admin: true,
      obj: fd,
    });
    if (res?.data?.success) set(field, res.data.filename);
    setUploading((p) => ({ ...p, [field]: false }));
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    await hitAxios({
      path: "/api/web/save_web_public",
      post: true,
      admin: true,
      obj: {
        ...form,
        custom_homepage_enabled: form.custom_homepage_enabled ? 1 : 0,
      },
    });
    setSaving(false);
  };

  const mediaUrl = (name) => (name ? `/media/${name}` : null);

  // ── HTML editor field ─────────────────────────────────────────────────────
  const HtmlField = ({ field, label, hint }) => (
    <Grid size={12}>
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {label}
          </Typography>
          <Chip
            label={lang.htmlSupported || "HTML Supported"}
            size="small"
            variant="outlined"
            color="info"
            sx={{ fontSize: 10, height: 20 }}
          />
        </Stack>
        <TextField
          fullWidth
          multiline
          rows={14}
          value={form[field]}
          onChange={(e) => set(field, e.target.value)}
          placeholder={hint}
          InputProps={{
            sx: {
              fontFamily: "monospace",
              fontSize: 13,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              "& textarea": { lineHeight: 1.7 },
            },
          }}
        />
        {form[field] && (
          <Box
            sx={{
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2.5,
              maxHeight: 220,
              overflowY: "auto",
              bgcolor: alpha(theme.palette.background.paper, 0.6),
            }}
          >
            <Typography
              variant="caption"
              color="text.disabled"
              display="block"
              mb={1}
              fontWeight={700}
              letterSpacing={1}
              sx={{ textTransform: "uppercase" }}
            >
              {lang.preview || "Preview"}
            </Typography>
            <Box
              sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.8 }}
              dangerouslySetInnerHTML={{ __html: form[field] }}
            />
          </Box>
        )}
      </Stack>
    </Grid>
  );

  return (
    <Box>
      <PageHeader
        title={lang.siteSettings || "Site Settings"}
        subtitle={lang.siteSettingsSub || "Manage site logo, SEO and tags"}
        icon={Language}
        primaryAction={
          <Button
            variant="contained"
            size="large"
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveOutlined />
              )
            }
            onClick={handleSave}
            disabled={saving}
            sx={{ borderRadius: 2, px: 4, fontWeight: 700 }}
          >
            {saving
              ? lang.saving || "Saving..."
              : lang.saveSettings || "Save Settings"}
          </Button>
        }
      />

      <Box sx={{ mt: 3 }}>
        {/* ── BRANDING ─────────────────────────────────────────────────────── */}
        <Section
          title={lang.branding || "Branding"}
          subtitle={lang.brandingDesc || "Your site name, logo and favicon"}
          icon={BrushOutlined}
          iconColor={theme.palette.primary.main}
        >
          <Grid container spacing={3}>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label={lang.siteName || "Site Name"}
                value={form.site_name}
                onChange={(e) => set("site_name", e.target.value)}
              />
            </Grid>

            {/* Logo */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                mb={1.5}
                display="block"
                fontWeight={600}
              >
                {lang.siteLogo || "Site Logo"}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                {form.site_logo ? (
                  <Avatar
                    src={mediaUrl(form.site_logo)}
                    variant="rounded"
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      border: `1px dashed ${theme.palette.divider}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <BrushOutlined
                      sx={{
                        fontSize: 20,
                        color: alpha(theme.palette.primary.main, 0.3),
                      }}
                    />
                  </Box>
                )}
                <Stack spacing={0.5}>
                  <Button
                    component="label"
                    size="small"
                    variant="outlined"
                    startIcon={
                      uploading.site_logo ? (
                        <CircularProgress size={14} />
                      ) : (
                        <UploadFileOutlined />
                      )
                    }
                    disabled={uploading.site_logo}
                    sx={{ borderRadius: 2 }}
                  >
                    {lang.upload || "Upload"}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, "site_logo")}
                    />
                  </Button>
                  {form.site_logo && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{
                        maxWidth: 160,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {form.site_logo}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Grid>

            {/* Favicon */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                mb={1.5}
                display="block"
                fontWeight={600}
              >
                {lang.siteFavicon || "Favicon (auto converted to 64×64 .ico)"}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                {form.site_favicon ? (
                  <Avatar
                    src={`${process.env.REACT_APP_BASE_URL}/favicon.ico?t=${Date.now()}`}
                    variant="rounded"
                    sx={{
                      width: 32,
                      height: 32,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      border: `1px dashed ${theme.palette.divider}`,
                    }}
                  />
                )}
                <Button
                  component="label"
                  size="small"
                  variant="outlined"
                  startIcon={
                    uploading.site_favicon ? (
                      <CircularProgress size={14} />
                    ) : (
                      <UploadFileOutlined />
                    )
                  }
                  disabled={uploading.site_favicon}
                  sx={{ borderRadius: 2 }}
                >
                  {lang.upload || "Upload"}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleFileChange(e, "site_favicon", "favicon")
                    }
                  />
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Section>

        {/* ── SEO ──────────────────────────────────────────────────────────── */}
        <Section
          title={lang.seoMeta || "SEO & Meta"}
          subtitle={
            lang.seoMetaDesc || "Control how search engines index your site"
          }
          icon={SearchOutlined}
          iconColor="#F59E0B"
        >
          <Grid container spacing={3}>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label={lang.metaTitle || "Meta Title"}
                value={form.meta_title}
                onChange={(e) => set("meta_title", e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label={lang.metaDescription || "Meta Description"}
                value={form.meta_description}
                onChange={(e) => set("meta_description", e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label={lang.metaKeywords || "Meta Keywords"}
                value={form.meta_keywords}
                onChange={(e) => set("meta_keywords", e.target.value)}
                helperText={lang.commaSeparated || "Comma separated"}
              />
            </Grid>
          </Grid>
        </Section>

        {/* ── OPEN GRAPH ───────────────────────────────────────────────────── */}
        <Section
          title={lang.openGraph || "Open Graph (OG)"}
          subtitle={
            lang.openGraphDesc ||
            "How your site appears when shared on social media"
          }
          icon={ShareOutlined}
          iconColor="#8B5CF6"
        >
          <Grid container spacing={3}>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label={lang.ogTitle || "OG Title"}
                value={form.og_title}
                onChange={(e) => set("og_title", e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label={lang.ogDescription || "OG Description"}
                value={form.og_description}
                onChange={(e) => set("og_description", e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                mb={1.5}
                display="block"
                fontWeight={600}
              >
                {lang.ogImage || "OG Image"}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                {form.og_image && (
                  <Box
                    component="img"
                    src={mediaUrl(form.og_image)}
                    sx={{
                      height: 60,
                      borderRadius: 1.5,
                      border: `1px solid ${theme.palette.divider}`,
                      objectFit: "cover",
                    }}
                  />
                )}
                <Button
                  component="label"
                  size="small"
                  variant="outlined"
                  startIcon={
                    uploading.og_image ? (
                      <CircularProgress size={14} />
                    ) : (
                      <UploadFileOutlined />
                    )
                  }
                  disabled={uploading.og_image}
                  sx={{ borderRadius: 2 }}
                >
                  {lang.upload || "Upload"}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "og_image")}
                  />
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Section>

        {/* ── ANALYTICS ────────────────────────────────────────────────────── */}
        <Section
          title={lang.analyticsTracking || "Analytics & Tracking"}
          subtitle={
            lang.analyticsTrackingDesc ||
            "Connect your analytics and tracking pixels"
          }
          icon={BarChartOutlined}
          iconColor="#10B981"
        >
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={lang.googleAnalyticsId || "Google Analytics ID"}
                placeholder="G-XXXXXXXXXX"
                value={form.google_analytics_id}
                onChange={(e) => set("google_analytics_id", e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={lang.googleTagManagerId || "Google Tag Manager ID"}
                placeholder="GTM-XXXXXXX"
                value={form.google_tag_manager_id}
                onChange={(e) => set("google_tag_manager_id", e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={lang.facebookPixelId || "Facebook Pixel ID"}
                placeholder="XXXXXXXXXXXXXXXXXX"
                value={form.facebook_pixel_id}
                onChange={(e) => set("facebook_pixel_id", e.target.value)}
              />
            </Grid>
          </Grid>
        </Section>

        {/* ── HOMEPAGE ─────────────────────────────────────────────────────── */}
        <Section
          title={lang.homepage || "Homepage"}
          subtitle={lang.homepageDesc || "Configure your homepage behaviour"}
          icon={HomeOutlined}
          iconColor="#3B82F6"
        >
          <Grid container spacing={3}>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.custom_homepage_enabled}
                    onChange={(e) =>
                      set("custom_homepage_enabled", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={500}>
                    {lang.enableCustomHomepage || "Enable Custom Homepage"}
                  </Typography>
                }
              />
            </Grid>
            {form.custom_homepage_enabled && (
              <Grid size={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={lang.customHomepageUrl || "Custom Homepage URL"}
                  placeholder="https://example.com"
                  value={form.custom_homepage_url}
                  onChange={(e) => set("custom_homepage_url", e.target.value)}
                />
              </Grid>
            )}
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label={lang.youtubeTutorialUrl || "YouTube Tutorial URL"}
                placeholder="https://youtube.com/watch?v=..."
                value={form.youtube_tutorial_url}
                onChange={(e) => set("youtube_tutorial_url", e.target.value)}
              />
            </Grid>
          </Grid>
        </Section>

        {/* ── CURRENCY ─────────────────────────────────────────────────────── */}
        <Section
          title={lang.currency || "Currency"}
          subtitle={
            lang.currencyDesc || "Set your platform's currency settings"
          }
          icon={AttachMoneyOutlined}
          iconColor="#F59E0B"
        >
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={lang.currencySymbol || "Currency Symbol"}
                placeholder="$"
                value={form.currency_symbol}
                onChange={(e) => set("currency_symbol", e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={lang.currencyCode || "Currency Code"}
                placeholder="USD"
                value={form.currency_code}
                onChange={(e) => set("currency_code", e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={lang.currencyExchangeRate || "Exchange Rate"}
                placeholder="1.0000"
                type="number"
                value={form.currency_exchange_rate}
                onChange={(e) => set("currency_exchange_rate", e.target.value)}
              />
            </Grid>
          </Grid>
        </Section>

        {/* ── PRIVACY POLICY ───────────────────────────────────────────────── */}
        <Section
          title={lang.privacyPolicy || "Privacy Policy"}
          subtitle={lang.privacyPolicyDesc || "Displayed on your /privacy page"}
          icon={PrivacyTipOutlined}
          iconColor="#EF4444"
        >
          <Grid container>
            <HtmlField
              field="privacy_policy_html"
              label={lang.privacyPolicyHtml || "Privacy Policy Content"}
              hint={`<h2>Privacy Policy</h2>\n<p>Your privacy policy content here...</p>`}
            />
          </Grid>
        </Section>

        {/* ── TERMS & CONDITIONS ───────────────────────────────────────────── */}
        <Section
          title={lang.termsConditions || "Terms & Conditions"}
          subtitle={lang.termsConditionsDesc || "Displayed on your /terms page"}
          icon={GavelOutlined}
          iconColor="#6366F1"
        >
          <Grid container>
            <HtmlField
              field="tnc_html"
              label={lang.tncHtml || "Terms & Conditions Content"}
              hint={`<h2>Terms & Conditions</h2>\n<p>Your terms and conditions here...</p>`}
            />
          </Grid>
        </Section>

        {/* ── ABOUT US ─────────────────────────────────────────────────────── */}
        <Section
          title={lang.aboutUs || "About Us"}
          subtitle={lang.aboutUsDesc || "Displayed on your /about page"}
          icon={InfoOutlined}
          iconColor="#0EA5E9"
        >
          <Grid container>
            <HtmlField
              field="about_us_html"
              label={lang.aboutUsHtml || "About Us Content"}
              hint={`<h2>About Us</h2>\n<p>Tell your story here...</p>`}
            />
          </Grid>
        </Section>
      </Box>
    </Box>
  );
};

export default SiteSettings;
